/*
 * tp-ex-best.js — „Die besten Käufe" als eigenständige Leiste (Collection + PDP).
 *
 * Dieselbe Rechnung wie die Preis-Leistungs-Karte, nur ohne Karte: tp-explorer-core.js
 * liefert Punkte (build), Leistungswert (score) und die beste Linie (pareto). Hier steht
 * nur, welcher Ausschnitt gezeigt wird und wie er aussieht.
 *
 * Zwei Modi am Element (data-mode):
 *   best  — die besten Käufe im Rahmen der Seite (Collection).
 *   near  — Einordnung des angezeigten Geräts (PDP): ein Satz, wo es steht, und
 *           die Angebote auf der besten Linie um seinen Preis herum.
 *
 * Im Unterschied zum Explorer ist das hier mehrfach pro Seite instanzierbar: kein
 * Modulglobal je Instanz, der Snapshot wird pro URL genau einmal geladen und geteilt.
 * Geladen wird erst, wenn die Leiste in die Nähe des Sichtfensters kommt.
 *
 * Karten kommen als echte Produktkarte über die Section-Rendering-API
 * (sections/tp-explorer-card.liquid) — Energielabel, Grundpreis und Zustand sind auf
 * einer Karte mit Preis Pflicht, ein Eigenbau darf hier nie die Endanzeige sein.
 */
(function () {
  'use strict';

  var C = window.TPExplorerCore;
  if (!C) { return; }

  /* ---------- geteilt über alle Instanzen ---------- */
  var snaps = {};                 // url -> Promise(data|null)
  var cards = {};                 // handle -> html|null
  var pending = {}, queue = [], inflight = 0, CARD_V = '';

  function snapshot(url) {
    if (!snaps[url]) {
      snaps[url] = fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return snaps[url];
  }

  /* Höchstens sechs Kartenanfragen gleichzeitig — sonst ist bei mehreren Leisten auf
     einer Seite der Shop-Server die Bremse. Muster aus tp-explorer.js. */
  function fetchCard(handle, done) {
    if (cards[handle] !== undefined) { done(); return; }
    if (pending[handle]) { pending[handle].push(done); return; }
    if (inflight >= 6) { queue.push([handle, done]); return; }
    pending[handle] = [done];
    inflight++;
    fetch('/products/' + encodeURIComponent(handle) + '?section_id=tp-explorer-card' + CARD_V,
      { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var t = document.createElement('template');
        t.innerHTML = html || '';
        var el = t.content.querySelector('[data-tp-ex-card]');
        cards[handle] = el ? el.innerHTML : null;
      })
      .catch(function () { cards[handle] = null; })
      .then(function () {
        var waiting = pending[handle] || [];
        delete pending[handle];
        inflight--;
        waiting.forEach(function (f) { f(); });
        var next = queue.shift();
        if (next) { fetchCard(next[0], next[1]); }
      });
  }

  /* ---------- Bausteine (Kopien aus tp-explorer.js, bewusst ohne Sheet-Anbindung) ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function eur(cents) {
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }
  function stoLabel(st) { return st >= 1024 ? (st / 1024) + ' TB' : st + ' GB'; }
  function hOf(p) { return (p.act && p.act.h) || p.h; }

  function secInfo(m, now) {
    var left = m.security_until - now, span = Math.max(1, m.security_until - m.release_year);
    return {
      w: Math.max(4, Math.round(Math.max(0, left) / span * 100)),
      cls: left >= 3 ? 'is-long' : (left === 2 ? 'is-mid' : (left === 1 ? 'is-short' : 'is-end')),
      txt: m.security_until < now ? 'Keine Sicherheitsupdates mehr' : 'Updates bis ' + m.security_until
    };
  }

  /* Die Zahl bekommt ihr Wort mit auf die Karte — „Note" als Kurzform der Gesamtnote,
     weil „Gesamtnote" den Fuß sprengt: dort stehen links schon „Updates bis 2032" und
     rechts der Ring, die 228 px Innenbreite sind damit fast voll. Der Tooltip und die
     Erklärzeile über der Leiste schreiben das Wort aus. Ohne Label liest sie fast jeder falsch,
     und jede Fehldeutung geht nach unten: als Schulnote ist 53 „fast durchgefallen", als
     Sternebewertung ein schlechtes Produkt — und bei generalüberholter Ware liegt der
     Schluss „Akku noch 53 %" am nächsten, weil genau so Akkuzustände angegeben werden.
     Die Erklärzeile über der Leiste fängt das nicht auf: Sie steht zu weit weg und wird
     von den meisten nicht gelesen.

     Der Fuß ist Anzeige, kein Weg: Update-Leiste über die volle Breite, darunter bis wann
     es Updates gibt, rechts die Gesamtnote. In der Kaufliste des Explorers steht hier
     zusätzlich „Preise & Details" — dort öffnet die Zeile ein Sheet mit allen Farben und
     Preisen des Modells. Ohne dieses Sheet wäre sie nur ein zweiter Link auf dieselbe
     Produktseite, auf die schon die Karte darüber führt. Dann lieber nichts. */
  function cardHTML(p, n, now, self) {
    var s = secInfo(p.m, now), h = hOf(p), v = Math.round(p.v), card = cards[h];
    return '<article class="tp-exb__c' + (p === self ? ' is-self' : '') + '" role="listitem">'
      + (n ? '<span class="tp-exb__n" aria-hidden="true">' + n + '</span>' : '')
      + '<div class="tp-exb__card" data-ch="' + esc(h) + '">'
      + (card === undefined ? '<span class="tp-exb__skel"></span>' : (card || fallback(p)))
      + '</div>'
      + '<div class="tp-exb__foot">'
      + '<span class="tp-exb__bar ' + s.cls + '"><i style="width:' + s.w + '%"></i></span>'
      + '<span class="tp-exb__upd">' + s.txt + '</span>'
      + '<span class="tp-exb__score">'
      + '<span class="tp-exb__lbl">Note</span>'
      + '<span class="tp-exb__ring" style="--p:' + v + '" title="Gesamtnote ' + v + ' von 100 \u2014 aus Kamera, Akku, Leistung, Speicher, Modelljahr und Sicherheitsupdates">'
      + '<b>' + v + '</b></span></span>'
      + '</div></article>';
  }

  /* Rückfall, wenn die Section-API die Karte nicht liefert (Produkt weg, Netz).
     Ohne Preis — eine Karte mit Preis braucht Energielabel und Grundpreis, und die
     kann nur das Theme rendern. */
  function fallback(p) {
    return '<a class="tp-exb__fb" href="/products/' + encodeURIComponent(hOf(p)) + '">'
      + esc(p.m.name + ' · ' + stoLabel(p.st)) + '</a>';
  }

  /* Die Marke „Dieses Gerät" gehört in die Badge-Spalte der Produktkarte, nicht als
     eigenes Schild darüber: dort stapeln sich schon TOP-SELLER, DEAL und RAUSVERKAUF in
     einer Spalte mit festem Abstand. Ein zweites Schild an derselben Ecke hätte sie
     verdeckt und wäre in Form und Farbe aus der Reihe gefallen. Fehlt die Spalte (Karte
     ohne Badges), wird sie angelegt. */
  function markSelf(slot) {
    var pc = slot.querySelector('.tp-pc');
    if (!pc || pc.querySelector('.tp-bdg--self')) { return; }
    var box = pc.querySelector('.tp-pc__badges');
    if (!box) {
      box = document.createElement('div');
      box.className = 'tp-pc__badges';
      pc.insertBefore(box, pc.firstChild);
    }
    var b = document.createElement('span');
    b.className = 'tp-bdg tp-bdg--self';
    b.textContent = 'Dieses Gerät';
    box.insertBefore(b, box.firstChild);
  }

  /* Aus der besten Linie gleichmäßig über die Preisspanne auswählen — die ersten N
     wären sonst immer die billigsten und die Leiste damit eine Ramschliste. */
  function spread(front, max) {
    if (front.length <= max) { return front.slice(); }
    var out = [], i, k;
    for (i = 0; i < max; i++) {
      k = Math.round(i * (front.length - 1) / (max - 1));
      if (out.indexOf(front[k]) < 0) { out.push(front[k]); }
    }
    return out;
  }

  /* ---------- eine Instanz ---------- */
  function run(el) {
    var url = el.getAttribute('data-url');
    var mode = el.getAttribute('data-mode') || 'best';
    var max = parseInt(el.getAttribute('data-max'), 10) || 6;
    var rail = el.querySelector('[data-exb-rail]');
    CARD_V = el.getAttribute('data-cardv') || CARD_V;

    snapshot(url).then(function (data) {
      if (!data || !data.models || !data.offers) { return drop(el); }
      var now = data.now_year || new Date().getFullYear();
      var pts = C.build(data);
      C.score(pts, C.PRESETS[0].w);

      var q = {
        brand: el.getAttribute('data-brand') || null,
        fams: (el.getAttribute('data-fams') || '').split('+').filter(Boolean),
        sto: 0,
        gr: el.getAttribute('data-gr') || 'C',
        cond: el.getAttribute('data-cond') || null,
        pmin: parseInt(el.getAttribute('data-pmin'), 10) || 0,
        pmax: parseInt(el.getAttribute('data-pmax'), 10) || 99999,
        upd: parseInt(el.getAttribute('data-upd'), 10) || 0,
        color: null,
        now: now
      };
      var list, self = null, selfPx = 0;

      if (mode === 'near') {
        self = find(pts, el.getAttribute('data-handle'));
        if (!self) { return drop(el); }
        selfPx = self.px;
        /* Auf der PDP wird die Rechnung an die Marke gebunden. Über alle Marken
           hinweg liefert die beste Linie zwar dasselbe Ergebnis wie die große Karte,
           aber auf einer iPhone-Seite heißt das „nimm ein Galaxy" — das ist keine
           Beratung, sondern ein Abwerbeversuch von dem Gerät, das gerade offen ist.
           Wer die Marke wechseln will, geht über den Link auf die ganze Karte. */
        q.brand = C.brandKey(self.m);
        brandLink(el, q.brand);
      }

      var ok = pts.filter(function (p) { return C.pass(p, q); });
      var front = C.pareto(ok, parseInt(el.getAttribute('data-minv'), 10) || 0);

      if (mode === 'near') {
        /* pass() setzt p.act auf die günstigste Farbe — für den eigenen Punkt gilt
           aber der Preis der Variante, die auf dieser Seite steht. */
        self.px = selfPx;
        say(el, self, front, now);
        /* Das angezeigte Gerät steht selbst mit in der Reihe, und zwar an seinem Platz:
           links davon, was günstiger ist, rechts davon, was mehr kann. Ohne es fehlt der
           Bezugspunkt — sechs fremde Karten sagen nicht, ob man gerade zu viel zahlt.
           Genommen werden die nächsten Nachbarn auf der besten Linie, nicht irgendein
           Preisfenster: die Linie ist dicht, und wer direkt daneben liegt, ist der
           Vergleich, den der Kunde im Kopf hat. */
        var links = front.filter(function (p) { return p !== self && p.px < selfPx; });
        var rechts = front.filter(function (p) { return p !== self && p.px > selfPx; });
        var haelfte = Math.floor((max - 1) / 2);
        var vorne = links.slice(Math.max(0, links.length - haelfte));
        var hinten = rechts.slice(0, max - 1 - vorne.length);
        /* Bleibt auf einer Seite Platz übrig (das Gerät ist das günstigste oder das
           teuerste), füllt die andere Seite auf — sonst stünde eine halbe Leiste da. */
        if (vorne.length < haelfte) { hinten = rechts.slice(0, max - 1 - vorne.length); }
        else if (hinten.length < max - 1 - haelfte) {
          vorne = links.slice(Math.max(0, links.length - (max - 1 - hinten.length)));
        }
        list = vorne.concat([self], hinten);
      } else {
        list = spread(front, max);
      }

      /* Eine Leiste mit zwei Karten ist keine Auswahl, sondern ein Zufall. */
      if (list.length < 3) { return drop(el); }

      rail.innerHTML = list.map(function (p, k) {
        return cardHTML(p, mode === 'best' ? k + 1 : 0, now, self);
      }).join('');
      el.removeAttribute('data-loading');
      count(el, ok.length);

      /* Das eigene Gerät in die Mitte der Leiste rollen. Nur der Track wird gescrollt,
         die Seite steht still — sonst springt die PDP beim Laden. */
      var ichEl = rail.querySelector('.is-self');
      if (ichEl) {
        rail.scrollLeft = ichEl.offsetLeft - (rail.clientWidth - ichEl.offsetWidth) / 2;
      }

      list.forEach(function (p) {
        var h = hOf(p);
        fetchCard(h, function () {
          var slot = rail.querySelector('[data-ch="' + h.replace(/["\\]/g, '') + '"]');
          if (!slot) { return; }
          slot.innerHTML = cards[h] || fallback(p);
          if (p === self) { markSelf(slot); }
        });
      });
    }).catch(function (e) {
      /* Ohne diesen Zweig verschluckt das Promise jeden Fehler: die Leiste bliebe
         stumm im Skelett stehen, und in der Konsole stünde nichts. Lieber weg und
         laut — ein Platzhalter über dem Grid, der nie zu Ende lädt, ist schlimmer
         als gar keine Leiste. */
      if (window.console) { console.error('[tp-ex-best]', e); }
      drop(el);
    });
  }

  function find(pts, handle) {
    if (!handle) { return null; }
    for (var i = 0; i < pts.length; i++) {
      var p = pts[i];
      if (p.h === handle) { p.act = C.active(p, null); p.px = p.act.price; return p; }
      if (p.cols) {
        for (var k = 0; k < p.cols.length; k++) {
          if (p.cols[k].h === handle) { p.act = p.cols[k]; p.px = p.cols[k].price; return p; }
        }
      }
    }
    return null;
  }

  /* Der Satz, der die PDP-Leiste trägt. Er sagt, was die Rechnung über DIESES Gerät
     ergibt — nicht, was es kann. Keine Wertung ohne Zahl dahinter.

     Jeder dieser Sätze passt in EINE Zeile. Über der Leiste stehen sonst drei Textzeilen
     übereinander, bevor die erste Karte kommt: Überschrift, Methodenzeile, Befund. Was
     man ohnehin sieht, gehört nicht in den Satz — „daneben steht, was anschließt" stand
     direkt über den Karten, die genau das zeigen. */
  function say(el, self, front, now) {
    var slot = el.querySelector('[data-exb-say]');
    if (!slot) { return; }
    var onFront = front.indexOf(self) > -1;
    var txt;
    if (self.m.security_until < now) {
      txt = 'Keine Sicherheitsupdates mehr — die Angebote hier bekommen noch welche.';
    } else if (onFront) {
      txt = 'Auf der besten Linie: Es gibt hier nichts Günstigeres, das mehr kann.';
    } else {
      var cheaper = null, better = null, i;
      for (i = 0; i < front.length; i++) {
        if (front[i].px <= self.px && front[i].v >= self.v && (!cheaper || front[i].px < cheaper.px)) {
          cheaper = front[i];
        }
        if (front[i].px > self.px && front[i].v > self.v + 4 && !better) { better = front[i]; }
      }
      if (cheaper && cheaper.px < self.px) {
        txt = 'Günstiger bei mindestens gleicher Note: ' + esc(cheaper.m.name) + ' ab '
          + eur(cheaper.px) + ' — ' + eur(self.px - cheaper.px) + ' weniger.';
      } else if (better) {
        txt = 'Für ' + eur(better.px - self.px) + ' mehr steht ' + esc(better.m.name)
          + ' deutlich besser da.';
      } else {
        txt = 'So steht dieses Gerät neben dem, was in seiner Preisklasse dasteht.';
      }
    }
    slot.innerHTML = txt;
  }

  /* Der Link führt ins Vollwerkzeug — auf der PDP mit derselben Marke vorbelegt, mit der
     hier gerechnet wurde. Feld 6 der Hash-Grammatik (fams,sto,gr,pmax,upd,color,brand,pmin).
     Die Marke steht erst nach dem Snapshot fest, Liquid kann sie nicht schreiben. */
  function brandLink(el, brand) {
    var a = el.querySelector('[data-exb-more]');
    if (!a || !brand) { return; }
    var parts = a.getAttribute('href').split('#e=');
    if (parts.length < 2) { return; }
    var f = parts[1].split(',');
    if (f.length < 8) { return; }
    f[6] = brand;
    a.setAttribute('href', parts[0] + '#e=' + f.join(','));
  }

  function count(el, n) {
    var slot = el.querySelector('[data-exb-count]');
    if (slot) { slot.textContent = n; }
  }

  /* Nichts Halbes stehen lassen: keine Daten, kein Treffer, zu wenig Auswahl —
     dann verschwindet die Leiste ganz, statt eine leere Überschrift zu zeigen.
     Gleiches Verhalten wie tp-gen-compare, wenn sein Datensatz fehlt. */
  function drop(el) { el.remove(); }

  /* Erst laden, wenn die Leiste in Sichtweite ist: auf der Collection steht sie über
     dem Grid und darf ihm weder Bandbreite noch das LCP wegnehmen. */
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) { return; }
      io.unobserve(e.target);
      run(e.target);
    });
  }, { rootMargin: '400px 0px' });

  /* Custom Element statt einmaligem querySelectorAll: Auf der Collection steht die
     Leiste INNERHALB von <results-list>, und jeder Filter- oder Sortierwechsel tauscht
     diesen Bereich über die Section-Rendering-API komplett aus. Ein Observer, der beim
     Laden des Skripts angehängt wurde, hinge danach an einem Knoten, den es nicht mehr
     gibt — die neue Leiste bliebe für immer im Skelett stehen. connectedCallback läuft
     bei jedem Einhängen erneut, der Snapshot-Cache oben bleibt bestehen (kein zweiter
     Netzabruf). */
  if (!customElements.get('tp-ex-best')) {
    customElements.define('tp-ex-best', class extends HTMLElement {
      connectedCallback() {
        if (this.__exbReady) { return; }
        this.__exbReady = true;
        io.observe(this);
      }
    });
  }
}());
